import { useCallback, useEffect, useState } from 'react';

// Results only change after a performance upload, so revisits within this
// window reuse the cached response; older entries still show at once and
// refresh quietly.
export const PERFORMANCE_CACHE_TTL_MS = 5 * 60 * 1000;

// A keyed response cache shared by every caller of the returned hook.
// `request(...args)` resolves to the response data.
export function createCachedResource(request, ttlMs = PERFORMANCE_CACHE_TTL_MS) {
  const cache = new Map();

  // Callers asking for the same response while a request is in flight share it.
  function load(key, args) {
    const entry = cache.get(key);
    if (entry?.request) return entry.request;

    const pending = request(...args).then(
      (data) => {
        cache.set(key, { data, fetchedAt: Date.now() });
        return data;
      },
      (err) => {
        const { request: _failed, ...previous } = cache.get(key) ?? {};
        if (previous.data) cache.set(key, previous);
        else cache.delete(key);
        throw err;
      },
    );
    cache.set(key, { ...entry, request: pending });
    return pending;
  }

  // A null key asks for nothing, so a caller can skip a request. The key
  // identifies the request, so `args` must only change along with it.
  function useCachedResource(key, args) {
    const [data, setData] = useState(() => (key === null ? null : cache.get(key)?.data ?? null));
    const [loading, setLoading] = useState(() => key !== null && !cache.get(key)?.data);
    const [error, setError] = useState(null);

    const fetchResource = useCallback(async ({ isCancelled = () => false, force = false } = {}) => {
      if (key === null) {
        setData(null);
        setError(null);
        setLoading(false);
        return;
      }
      const cached = cache.get(key);
      const showCached = Boolean(cached?.data) && !force;
      if (showCached) {
        setData(cached.data);
        setError(null);
        setLoading(false);
        if (Date.now() - cached.fetchedAt < ttlMs) return;
      } else {
        setLoading(true);
        setError(null);
      }
      try {
        const result = await load(key, args);
        if (!isCancelled()) setData(result);
      } catch (err) {
        // A failed quiet refresh keeps the cached response on screen.
        if (!isCancelled() && !showCached) setError(err);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      let cancelled = false;
      fetchResource({ isCancelled: () => cancelled });
      return () => { cancelled = true; };
    }, [fetchResource]);

    return { data, loading, error, refetch: () => fetchResource({ force: true }) };
  }

  return { clear: () => cache.clear(), useCachedResource };
}
